<template>
  <DialogFrame
    v-model:visible="visible"
    :loading="loading"
    @onHide="onHide"
    @onClose="closeModal"
    class="p-dialog-ultra-wide"
  >
    <template #title>
      Event Details
      <Button
        v-if="userStore.isMeshhAdmin"
        class="copy_button"
        outlined
        aria-label="Copy UUID"
        @click="copyToClipboard(toast, reviewEvent.uuid)"
      >
        <IconCopy /> Copy UUID
      </Button>
    </template>

    <template #body>
      <div class="p-dialog-event-review">
        <div class="event-form event__review">
          <section>
            <div class="event-form--content">
              <section class="event-review-section event-review-section--basics">
                <div class="event-review-basics__title">
                  {{ reviewEvent.name || 'No event name entered' }}
                </div>
                <div class="event-review-basics__details">
                  <IconAccount /> {{ reviewEvent.account.display_name }} <IconLocation />
                  {{ locationName }}
                </div>
                <div class="event-review-basics__dates">
                  <span>Start: {{ startDate }} {{ startTimeZone }}</span>
                  <span class="divider">|</span>
                  <span>End: {{ endDate }} {{ endTimeZone }}</span>
                </div>
              </section>

              <div class="event-chips-container">
                <section class="event-review-section event-review-section__chips">
                  <div class="event-review-section__title">
                    <span>Event Type</span>
                  </div>
                  <div class="event-review-section__content">
                    <template v-if="reviewEvent.event_type">
                      <Chip :key="reviewEvent.event_type" :isStatic="true">
                        {{
                          eventsStore.eventList?.event_type.find(
                            (evt) => evt.id === reviewEvent?.event_type,
                          )?.name
                        }}
                      </Chip>
                    </template>
                    <template v-else> No types chosen </template>
                  </div>
                </section>

                <section class="event-review-section event-review-section__chips">
                  <div class="event-review-section__title">
                    <span>Verticals</span>
                  </div>
                  <div class="event-review-section__content">
                    <template v-if="reviewEvent.verticals.length">
                      <Chip v-for="vertId in reviewEvent.verticals" :key="vertId" :isStatic="true">
                        {{ eventsStore.eventList?.vertical.find((evt) => evt.id === vertId)?.name }}
                      </Chip>
                    </template>
                    <template v-else> No verticals chosen </template>
                  </div>
                </section>
              </div>

              <section class="event-review-section">
                <div class="event-review-section__title">
                  <span>Schedule</span>
                </div>
                <div class="event-review-section__content">
                  <ul class="date-overrides" :class="{ 'date-overrides--overnight': isOvernight }">
                    <li class="date-overrides__header">
                      <span><template v-if="isOvernight">Start </template>Date</span>
                      <span>Start Time</span>
                      <span v-if="isOvernight">End Date</span>
                      <span>End Time</span>
                    </li>
                    <li v-for="date in reviewEvent.dates" :key="date.date">
                      <span>{{ dayjs(date.date).format('ddd, MMM DD, YYYY') }}</span>
                      <span>{{ dayjs(`${date.date} ${date.start}`).format('hh:mm A') }}</span>
                      <span v-if="isOvernight">
                        <template v-if="isDateOvernight(date)">
                          {{ dayjs(date.date).add(1, 'day').format('ddd, MMM DD, YYYY') }}
                        </template>
                      </span>
                      <span>{{ dayjs(`${date.date} ${date.end}`).format('hh:mm A') }}</span>
                    </li>
                  </ul>
                </div>
              </section>

              <EventReviewZones
                :newEvent="reviewEvent"
                :sensorDistanceOptions="sensorDistanceOptions"
              />

              <section class="event-review-section">
                <div class="event-review-section__title">
                  <span>Clusters ({{ reviewEvent.clusters.length }})</span>
                </div>

                <div
                  v-if="reviewEvent.clusters.length"
                  class="event-review-section__content event-review-section__split"
                >
                  <Tabs value="0">
                    <div class="split-section__list">
                      <div class="split-section__list-header">Cluster</div>
                      <TabList>
                        <Tab
                          v-for="(cluster, index) in reviewEvent.clusters"
                          :key="index"
                          :value="`${index}`"
                        >
                          {{ cluster.name }} ({{ cluster.zones?.length }})
                        </Tab>
                      </TabList>
                    </div>
                    <div class="split-section__details">
                      <div class="split-section__details-header">Zone Details</div>

                      <TabPanels>
                        <TabPanel
                          v-for="(cluster, index) in reviewEvent.clusters"
                          :key="index"
                          :value="`${index}`"
                        >
                          <ul class="zone-list">
                            <li v-for="(zone, index) in cluster.zones" :key="index">
                              {{ zone.name }} <Chip>1 Sensor</Chip>
                            </li>
                          </ul>
                        </TabPanel>
                      </TabPanels>
                    </div>
                  </Tabs>
                </div>
                <div v-else class="event-review-section__content">
                  <div class="event-review__empty-message">No clusters created</div>
                </div>
              </section>

              <section class="event-review-section">
                <div class="event-review-section__title">
                  <span>Notifications</span>
                  Event details will be sent to these email(s) or phone number(s)
                </div>
                <div
                  v-if="reviewEvent.notifications?.length"
                  class="event-review-section__content event-review-section__table"
                >
                  <div>Who to notify</div>
                  <table>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Phone Number</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(notification, index) in reviewEvent.notifications" :key="index">
                        <td>{{ notification.email }}</td>
                        <td>{{ notification.phone_number }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="event-review-section__content">
                  <div class="event-review__empty-message">No notifications set</div>
                </div>
              </section>
            </div>
          </section>
        </div>
      </div>
    </template>

    <template #footer>
      <Button
        :disabled="loading"
        label="Close"
        severity="primary"
        @click="closeModal()"
        autofocus
      />
    </template>
  </DialogFrame>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import toObject from 'dayjs/plugin/toObject';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(toObject);

import { getEventById, getSensorRangeOptions } from '@/utils/api';

import { Button, Tabs, TabList, Tab, TabPanels, TabPanel } from 'primevue';

import Chip from '@/components/global/Chip.vue';
import EventReviewZones from '../events/forms/EventReviewZones.vue';

import IconAccount from '@/icons/users/IconAccount.svg?component';
import IconCopy from '@/icons/events/IconCopy.svg?component';
import IconLocation from '@/icons/IconLocation.svg?component';

import type { SensorDistanceSettingType } from '@/types/Sensor';

import { useEventsStore } from '@/stores/events';
const eventsStore = useEventsStore();

import { useUserStore } from '@/stores/user';
import type { ClusterType, EventDate, ZoneType } from '@/types/Event';
const userStore = useUserStore();

import { copyToClipboard } from '@/utils/clipboardCopy';

import { useToastStore } from '@/utils/toast';
import DialogFrame from './DialogFrame.vue';
const toast = useToastStore();

const sensorDistanceOptions = ref<SensorDistanceSettingType[]>([]);

const props = defineProps<{
  eventId?: number;
}>();

const loading = ref(true);
const visible = ref(true);
const reviewEvent = ref();

const emit = defineEmits(['modalClosed']);

const closeModal = () => {
  reviewEvent.value = null;
  emit('modalClosed');
};

const onHide = () => {
  reviewEvent.value = null;
  emit('modalClosed');
};

const getEventData = () => {
  if (props.eventId) {
    loading.value = true;
    getEventById(userStore.accessToken, props.eventId).then((resp) => {
      loading.value = false;
      reviewEvent.value = resp.CampaignsEvent;

      reviewEvent.value.zones.map((zone: ZoneType) => {
        if (zone.sensor) {
          zone.sensor.engaged_signal = zone.range_engaged;
          zone.sensor.passerby_signal = zone.range_passerby;
        }
        return zone;
      });

      reviewEvent.value.clusters.map((cluster: ClusterType) => {
        const zones: ZoneType[] = [];

        reviewEvent.value.zones.forEach((zone: ZoneType) => {
          if (zone.tags?.includes(cluster.name)) {
            zones.push(zone);
          }
        });

        cluster.zones = zones;
        return cluster;
      });

      if (reviewEvent.value) {
        const firstZoneSensor = reviewEvent.value?.zones.length
          ? reviewEvent.value.zones[0].sensor_id
          : null;

        if (firstZoneSensor) {
          getSensorRangeOptions(userStore.accessToken, userStore.isStaff, firstZoneSensor).then(
            (resp) => {
              sensorDistanceOptions.value = resp;
            },
          );
        }
      }
    });
  }
};

const locationName = computed(() => {
  if (reviewEvent.value?.timezone_id) {
    const locMatch = eventsStore.eventList?.location.find(
      (val) => val.id === reviewEvent.value?.timezone_id,
    );
    return locMatch?.name || '';
  }
  return '';
});

const startDate = computed(() => {
  if (!reviewEvent.value || !reviewEvent.value.event_start) {
    return '';
  }

  return dayjs(reviewEvent.value.event_start).format('ddd, MMM DD, YYYY h:mmA');
});

const startTimeZone = computed(() => {
  if (!reviewEvent.value || !reviewEvent.value.event_start_tz) {
    return '';
  }

  return reviewEvent.value.event_start_tz;
});

const endDate = computed(() => {
  if (!reviewEvent.value || !reviewEvent.value.event_end) {
    return '';
  }

  return dayjs(reviewEvent.value.event_end).format('ddd, MMM DD, YYYY h:mmA');
});

const endTimeZone = computed(() => {
  if (!reviewEvent.value || !reviewEvent.value.event_end_tz) {
    return '';
  }

  return reviewEvent.value.event_end_tz;
});

const isOvernight = computed(() => {
  if (reviewEvent.value?.event_start && reviewEvent.value?.event_end) {
    const start = dayjs(reviewEvent.value?.event_start).hour();
    const end = dayjs(reviewEvent.value?.event_end).hour();

    return start >= end;
  }
  return false;
});

const isDateOvernight = (date: EventDate) => {
  if (date.start && date.end) {
    const start = parseInt(date.start.split(':')[0]);
    const end = parseInt(date.end?.split(':')[0]);

    return start >= end;
  }
  return false;
};

eventsStore.getEventListData();
getEventData();

watch(props, () => {
  visible.value = true;
  eventsStore.getEventListData();
  getEventData();
});
</script>

<style lang="scss">
.p-button.copy_button {
  @include mixins.flex-center($gap: variables.$vantage-base-sizing-xxxs);
  margin-left: auto;
  margin-right: variables.$vantage-base-sizing-sm;
}
</style>
